import React, { FC, useMemo, useEffect } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Button, CircularProgress } from '@mui/material';

import { Application } from 'reduxState/store/application/types';
import CampaignManagerImageUpload from 'components/Campaigns/CampaignForms/CampaignManagerImageUpload/CampaignManagerImageUploader';
import { displayTypeOptions, frequencyTypeOptions } from 'components/Campaigns/CampaignForms/constants';
import DropdownSearchSelect, { IDropdownSelectOption } from 'components/DropdownSearchSelect/DropdownSearchSelect';
import { ICampaign } from 'reduxState/store/campaigns/types';
import TextInput from '../../../TextInput/TextInput';
import MerchantSearchField from '../MerchantSearchField/MerchantSearchField';
import CampaignBulkAddField from '../CampaignBulkAddField/CampaignBulkAddField';
import { useCampaignForm } from '../hooks/useCampaignForm';
import CampaignDisplayTypeFields from '../CampaignDisplayTypeFields/CampaignDisplayTypeFields';
import CampaignURLPatternsField from '../CampaignURLPatternsField/CampaignURLPatternsField';

/**
 * EditCampaignForm component for editing existing marketing campaigns.
 *
 * This component provides a form interface for modifying campaign settings and configuration.
 * It handles validation, conditional field restrictions, and real-time updates while
 * preventing modifications to certain fields for active/expired campaigns.
 *
 * Key features:
 * - Edit campaign details like title, dates, merchant ID
 * - Configure content including headline, main text, and CTA settings
 * - Manage HTML content and insertion settings
 * - Set targeting rules via URL patterns and search phrases
 * - Upload and manage campaign images
 * - Validate form fields and handle errors
 * - Restrict editing based on campaign status
 *
 * @component
 * @param {Object} props - Component props
 * @param {Application[]} props.selectedApps - List of applications targeted by this campaign
 * @param {Function} props.setHasSelectedAppsError - Sets error state for app selection
 * @param {Function} props.resetSelectedApps - Resets selected applications to empty
 * @param {Function} props.setSelectedCampaignToEdit - Updates which campaign is being edited
 * @param {ICampaign} props.campaignToEdit - Campaign data to populate the form
 * @param {boolean} props.isEditingDisabled - Whether form is in read-only mode
 * @param {boolean} props.isStartDateDisabled - Whether start date field is locked
 * @returns {JSX.Element} Campaign editing form with validation and conditional restrictions
 */

interface IEditCampaignForm {
  selectedApps: Application[];
  campaignToEdit: ICampaign;
  isEditingDisabled: boolean;
  isStartDateDisabled: boolean;
  setHasSelectedAppsError: (hasError: boolean) => void;
  resetSelectedApps: () => void;
  setSelectedCampaignToEdit: (campaign: ICampaign | null) => void;
  handleCloseModal: () => void;
  /** Called when form dirty state changes (so parent can skip discard confirm when no changes). */
  onHasFormChangesChange?: (hasChanges: boolean) => void;
}

const EditCampaignForm: FC<IEditCampaignForm> = ({
  selectedApps,
  campaignToEdit,
  isEditingDisabled,
  isStartDateDisabled,
  setHasSelectedAppsError,
  resetSelectedApps,
  setSelectedCampaignToEdit,
  handleCloseModal,
  onHasFormChangesChange,
}) => {
  // Create a stable reference for minimum date to prevent validation issues as time passes
  const minDateTime = useMemo(() => new Date(), []);

  const {
    formData,
    setFormData,
    formErrors,
    setFormErrors,
    uploadedImages,
    networkMerchant,
    insertionOrderError,
    htmlPopUpTemplateFormData,
    hasAttemptedSubmit,
    setNetworkMerchant,
    isLoading,
    urlPatternsError,
    shouldUpdateStartDateToNow,
    handleInputChange,
    handleNumberInputChange,
    handleAddUrlPattern,
    handleRemoveUrlPattern,
    handleUpdateUrlPattern,
    handleAddSearchPhrase,
    handleRemoveSearchPhrase,
    handleSetUploadedImages,
    setHTMLPopUpTemplateFormData,
    setUrlPatternsError,
    handleSubmit,
    handleCancel,
    handleStartDateManualChange,
    handleStartDateNowClick,
    hasFormChanges,
  } = useCampaignForm({
    mode: 'edit',
    selectedApps,
    setHasSelectedAppsError,
    resetSelectedApps,
    handleCloseModal,
    campaignToEdit,
    setSelectedCampaignToEdit,
  });

  useEffect(() => {
    onHasFormChangesChange?.(hasFormChanges);
  }, [hasFormChanges, onHasFormChangesChange]);

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 font-montserrat">
      <div>
        <h5 className="text-muted-dark-purple">Campaign Details</h5>
        <div className="flex flex-col flex-1">
          <DropdownSearchSelect
            value={formData.displayType}
            label="Display Type"
            options={displayTypeOptions}
            placeholder="Select a Display Type"
            handleChange={(option: IDropdownSelectOption | null) => {
              if (option) {
                setFormErrors({ displayType: false });
              }
              setFormData(prev => ({ ...prev, displayType: option }));
            }}
            isDisabled={true}
            hasError={formErrors.displayType}
            errorMessage={'Display type is required'}
          />
        </div>
        <div className="flex flex-col flex-1">
          <TextInput
            label="Campaign Title"
            value={formData.title}
            placeholderText="Enter Campaign Title"
            handleChange={(value: string) => {
              handleInputChange(value, 'title');
            }}
            hasError={formErrors.title}
            errorText={'Title is required'}
            isDisabled={isEditingDisabled}
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-4 my-[8px]">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div className="flex-1 min-w-0">
              <div className="flex flex-col xs:flex-row gap-2 items-stretch xs:items-center">
                <Button
                  className="bg-light-grey text-dark-grey hover:bg-dark-purple hover:text-white disabled:opacity-50 disabled:cursor-not-allowed normal-case rounded-2xl shrink-0"
                  disabled={isStartDateDisabled || isEditingDisabled}
                  onClick={handleStartDateNowClick}
                >
                  Now
                </Button>
                <DateTimePicker
                  label={shouldUpdateStartDateToNow ? 'Will get start date/time at submission' : 'Start Date'}
                  value={shouldUpdateStartDateToNow ? null : formData.startDate}
                  minDateTime={
                    isStartDateDisabled || isEditingDisabled ? new Date(campaignToEdit.startDate) : minDateTime
                  }
                  closeOnSelect={false}
                  disabled={isStartDateDisabled || isEditingDisabled}
                  onChange={handleStartDateManualChange}
                  sx={{
                    width: '100%',
                    maxWidth: 280,
                  }}
                  slotProps={{
                    textField: {
                      error: formErrors.startDate,
                      helperText: formErrors.startDate ? 'Start date is required' : undefined,
                    },
                  }}
                />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <DateTimePicker
                label="End Date"
                value={formData.endDate}
                minDateTime={isEditingDisabled ? new Date(campaignToEdit.endDate) : (formData.startDate || minDateTime)}
                closeOnSelect={false}
                disabled={isEditingDisabled}
                onChange={(date: Date | null) => {
                  if (date) {
                    setFormErrors(prev => ({ ...prev, endDate: false }));
                    setFormData(prev => ({ ...prev, endDate: date }));
                  }
                }}
                sx={{
                  width: '100%',
                  maxWidth: 280,
                }}
                slotProps={{
                  textField: {
                    error: formErrors.endDate,
                    helperText: formErrors.endDate ? 'End date is required' : undefined,
                  },
                }}
              />
            </div>
          </LocalizationProvider>
        </div>
        <div className="flex flex-col flex-1">
          <MerchantSearchField
            value={formData.merchantId}
            onChange={(value: string) => handleInputChange(value, 'merchantId')}
            label="Merchant ID"
            placeholder="Search by merchant name or enter Merchant ID"
            hasError={formErrors.merchantId}
            errorText="Merchant ID is required"
            isDisabled={isEditingDisabled}
          />
        </div>
        <div className="flex flex-col flex-1">
          <TextInput
            label="Destination URL"
            value={formData.destinationURL}
            placeholderText="Enter Destination URL"
            handleChange={(value: string) => {
              handleInputChange(value, 'destinationURL');
            }}
            hasError={formErrors.destinationURL}
            errorText={'Valid Destination URL is required'}
            isDisabled={isEditingDisabled}
            tooltipText="The landing page URL where users will be directed when they click the campaign"
          />
        </div>

        <div className="flex flex-col flex-1 mb-2">
          <TextInput
            label="Search Results Tagline"
            value={formData.serpTagline}
            hasError={formErrors.serpTagline}
            placeholderText="Enter Search Results Tagline"
            handleChange={(value: string) => {
              handleInputChange(value, 'serpTagline');
            }}
            isDisabled={isEditingDisabled}
            tooltipText="Text that appears in search engine results pages to describe this campaign"
          />
        </div>
        <div className="flex flex-col flex-1 mb-2">
          <TextInput
            label="Google Sheet ID"
            value={formData.googleSheetID}
            placeholderText="Enter Google Sheet ID"
            handleChange={(value: string) => {
              handleInputChange(value, 'googleSheetID');
            }}
            isDisabled={isEditingDisabled}
          />
        </div>
        <div className="flex gap-4">
          <div className="flex flex-col flex-1">
            <TextInput
              label="Insertion Order ID"
              value={formData.insertionOrderId}
              placeholderText="Enter Insertion Order ID"
              handleChange={(value: string) => {
                handleInputChange(value, 'insertionOrderId');
                setNetworkMerchant(null);
              }}
              hasError={formErrors.insertionOrderId}
              errorText={insertionOrderError || 'Insertion Order ID is required'}
              isDisabled={isEditingDisabled}
            />
            {networkMerchant && (
              <p className="text-xs italic">Network Merchant: {`${networkMerchant?.id} - ${networkMerchant?.name}`}</p>
            )}
          </div>
          <div className="flex flex-col flex-1">
            <TextInput
              label="Insertion Order Value"
              value={formData.insertionOrderValue}
              placeholderText="Enter Insertion Order Value"
              handleChange={(value: string) => {
                handleInputChange(value, 'insertionOrderValue');
              }}
              hasError={formErrors.insertionOrderValue}
              errorText={'Insertion Order Value is required'}
              isDisabled={isEditingDisabled}
            />
          </div>
        </div>
        <CampaignURLPatternsField
          hasAttemptedSubmit={hasAttemptedSubmit}
          valuesArray={formData.urlPatterns2}
          handleAddUrlPattern={handleAddUrlPattern}
          handleRemoveUrlPattern={handleRemoveUrlPattern}
          hasError={formErrors.urlPatterns2}
          handleUpdateUrlPattern={handleUpdateUrlPattern}
          setUrlPatternsError={setUrlPatternsError}
          urlPatternsError={urlPatternsError}
        />
        <CampaignBulkAddField
          valueArray={formData.searchPhrases}
          handleAddValue={handleAddSearchPhrase}
          handleRemoveValue={handleRemoveSearchPhrase}
          error={formErrors.searchPhrases}
          title="Search Phrases"
          placeholder="Enter search phrase"
          disable={isEditingDisabled}
        />
        <div>
          <h5 className="mb-2 text-muted-dark-purple">Display Frequency Settings</h5>
          <p className="mb-3 text-sm text-gray-600">
            Control how often users see this campaign. These settings work together to prevent over-showing campaigns.
          </p>
          <div className="flex gap-4">
            <div className="flex flex-col flex-1">
              <DropdownSearchSelect
                value={formData.frequencyType}
                label="Type"
                options={frequencyTypeOptions}
                placeholder="Select a type "
                handleChange={(option: IDropdownSelectOption | null) => {
                  if (option) {
                    setFormErrors(prev => ({ ...prev, frequencyType: false }));
                  }
                  setFormData(prev => ({ ...prev, frequencyType: option }));
                }}
                hasError={formErrors.frequencyType}
                errorMessage={'Frequency type is required'}
                isDisabled={isEditingDisabled}
                tooltipText="DISMISSAL: Counts when user closes the campaign. VIEW_COUNT: Counts each time campaign is shown. Example: Use DISMISSAL for pop-ups that users can close, VIEW_COUNT for inline content."
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Minutes (Time Window)"
                value={formData.minutes !== null ? formData.minutes : ''}
                placeholderText="e.g., 1440 for 24 hours"
                inputType="number"
                inputProps={{ min: '0' }}
                handleChange={(value: string) => handleNumberInputChange(value, 'minutes')}
                hasError={formErrors.minutes}
                errorText={'Valid minute value is required'}
                isDisabled={isEditingDisabled}
                tooltipText="Time window for counting views/dismissals. Common values: 60 (1 hour), 1440 (24 hours), 10080 (1 week). Example: 1440 minutes = campaign can show up to [Limit] times per day."
              />
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex flex-col flex-1">
              <TextInput
                label="Limit (Max Shows)"
                inputType="number"
                placeholderText="e.g., 3"
                value={formData.limit !== null ? formData.limit : ''}
                inputProps={{ min: '0' }}
                handleChange={(value: string) => handleNumberInputChange(value, 'limit')}
                hasError={formErrors.limit}
                errorText={'Valid limit value is required'}
                isDisabled={isEditingDisabled}
                tooltipText="Maximum times campaign shows within the time window. Example: Limit=3, Minutes=1440 means 'show max 3 times per 24 hours'. After reaching limit, cooldown period starts."
              />
            </div>
            <div className="flex flex-col flex-1">
              <TextInput
                label="Cooldown (Hours)"
                inputType="number"
                placeholderText="e.g., 24"
                value={formData.cooldown !== null ? formData.cooldown : ''}
                inputProps={{ min: '0' }}
                handleChange={(value: string) => handleNumberInputChange(value, 'cooldown')}
                hasError={formErrors.cooldown}
                errorText={'Valid cooldown value is required'}
                isDisabled={isEditingDisabled}
                tooltipText="Hours to wait after hitting the limit before showing again. Example: Cooldown=24 means 'wait 24 hours after user sees campaign 3 times'. Use Case: Limit=1, Cooldown=168 = 'show once per week'."
              />
            </div>
          </div>
        </div>
        <CampaignManagerImageUpload
          uploadedImages={uploadedImages}
          setUploadedImages={handleSetUploadedImages}
          disabled={isEditingDisabled}
        />
        <CampaignDisplayTypeFields
          displayType={formData.displayType}
          formData={formData}
          setFormData={setFormData}
          setFormErrors={setFormErrors}
          formErrors={formErrors}
          setHTMLPopUpTemplateFormData={setHTMLPopUpTemplateFormData}
          htmlPopUpTemplateFormData={htmlPopUpTemplateFormData}
          isEditingDisabled={isEditingDisabled}
        />
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          className="w-[100px] h-[50px] px-4 py-2 mt-4 transition-colors rounded-md text-md bg-light-grey text-dark-grey font-semibold hover:bg-dark-purple hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="clear-button"
          onClick={() => handleCancel()}
        >
          Cancel
        </button>
        <Button
          type="submit"
          className="w-[200px] h-[50px] px-4 py-2 mt-4 font-bold text-white transition-colors rounded-md text-md bg-muted-dark-purple hover:bg-dark-purple disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="submit-button"
          disabled={isLoading || isEditingDisabled}
          endIcon={isLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
        >
          {isLoading ? 'Updating Campaign...' : 'Update Campaign'}
        </Button>
      </div>
    </form>
  );
};

export default EditCampaignForm;
